import React, { useState } from 'react';
import {
  useAppContext,
  useTranslator,
  cloudflareImage,
  mealPlaceholderImage,
  useToast,
  callNativeMobileApp,
} from '@front-activerse/common-components';
import { Button, Modal, ModalBody, ModalFooter, Row, Col } from 'reactstrap';

interface ITrackerMealComponentProps {
  id: number;
  name: string;
  calories: number;
  proteinGrams: number;
  carbsGrams: number;
  fatGrams: number;
  portions: number;
  imageUrl: string;
  mealType: number;
  loggedAt: string;
  sourceType: string;
  removeMeal: (mealId: number) => Promise<any>;
  date: string;
}

const formatTimeFromDate = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const TrackerMealComponent: React.FC<ITrackerMealComponentProps> = ({
  id,
  name,
  calories,
  proteinGrams,
  carbsGrams,
  fatGrams,
  portions,
  imageUrl,
  mealType,
  loggedAt,
  sourceType,
  removeMeal,
  date,
}) => {
  const [showAlertModal, setShowAlertModal] = useState(false);
  const toggleModal = () => {
    setShowAlertModal(!showAlertModal);
  };
  const { t } = useTranslator();
  const {
    images: { trashIcon },
    consts: { APP_BRAND },
  } = useAppContext();

  const { toastError } = useToast();

  const renderMealImage = () => {
    let mealImg;
    if (imageUrl) {
      mealImg = imageUrl;
    } else {
      mealImg = mealPlaceholderImage(mealType, APP_BRAND as string);
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'mealPlanThumbnail');

    return <img src={coverImage} className="tracker-history-item-icon" alt="" />;
  };

  const renderSourceType = () => {
    if (sourceType === 'cookbook') {
      return t('tracker.history.entryType.cookbook');
    } else if (sourceType === 'diet') {
      return t('tracker.history.entryType.mealPlan');
    }
  };

  const removeMealHandler = async () => {
    setShowAlertModal(false);
    const response = await removeMeal(id);
    if (response.code === 200) {
      // refresh native tracker data
      callNativeMobileApp({
        action: 'refresh_data',
        payload: {
          module: 'tracker',
          date: date,
        },
      });
    } else {
      toastError();
    }
  };

  return (
    <>
      <div className="tracker-history-item d-flex">
        {renderMealImage()}

        <div className="flex-grow-1 text-start">
          <div className="tracker-history-item-meta mb-xxs mt-s">
            <p className="description text-text-variant-color mb-0">
              <span>{formatTimeFromDate(loggedAt)}</span>
              <strong className="bullet-separator mx-1">•</strong>
              <span>{renderSourceType()}</span>
            </p>
          </div>
          <p className="small mb-xxs">{name}</p>
          <div className="d-flex justify-content-between">
            <p className="description text-text-main-color mb-s">
              <span role="img" aria-label="fruit">
                🍏
              </span>{' '}
              {calories} kcal
            </p>
            <p className="description text-text-variant-color mb-s">
              <span className="me-2">
                {t('tracker.history.proteins')}{' '}
                <span className="text-text-main-color">{proteinGrams}g</span>
              </span>
              <span className="me-2">
                {t('tracker.history.fats')}{' '}
                <span className="text-text-main-color">{fatGrams}g</span>
              </span>
              <span>
                {t('tracker.history.carbs')}{' '}
                <span className="text-text-main-color">{carbsGrams}g</span>
              </span>
            </p>
          </div>
        </div>

        <Button className="tracker-history-item-delete p-0" color="link" onClick={toggleModal}>
          <img src={trashIcon} alt="" />
        </Button>
      </div>

      <Modal isOpen={showAlertModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold mb-l mt-s">
            {t('tracker.history.removePopup.appMeal.title')}
          </h4>
          <p className="mb-0 text-text-variant-color">
            {t('tracker.history.removePopup.appMeal.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Row className="w-100 m-0">
            <Col xs="6" className="ps-0 pe-2">
              <Button color="secondary" outline className="w-100" onClick={() => toggleModal()}>
                {t('tracker.history.removePopup.cancelButton')}
              </Button>
            </Col>
            <Col xs="6" className="ps-2 pe-0">
              <Button
                color="primary"
                className="w-100"
                onClick={() => removeMealHandler()}
                disabled={!showAlertModal}
              >
                {t('tracker.history.removePopup.removeButton')}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TrackerMealComponent;
